import * as Sentry from '@sentry/react';

import ERROR_CODES from '@/core/apiErrorCodes';

import { ActivityRecord } from './ActivityGuardModal';

const ACTIVITY_GUARD_CODES = new Set<string>([
  ERROR_CODES.TEAM_MEMBER_HAS_ACTIVE_CONSULTATIONS,
  ERROR_CODES.TEAM_MEMBER_HAS_ACTIVE_REQUESTS,
]);

export interface ActivityGuardError {
  code: string;
  message: string;
  records: ActivityRecord[];
}

type GqlLikeError = {
  graphQLErrors?: ReadonlyArray<{
    message?: string;
    extensions?: { code?: string; records?: ActivityRecord[] };
  }>;
};

function asGqlError(err: unknown): GqlLikeError | null {
  if (typeof err !== 'object' || err === null || !('graphQLErrors' in err)) return null;
  return err as GqlLikeError;
}

export function extractActivityGuardError(err: unknown): ActivityGuardError | null {
  const gqlError = asGqlError(err)?.graphQLErrors?.[0];
  if (!gqlError) return null;
  const code = gqlError.extensions?.code;
  if (!code || !ACTIVITY_GUARD_CODES.has(code)) return null;
  return {
    code,
    message: gqlError.message ?? '',
    records: gqlError.extensions?.records ?? [],
  };
}

type NotifyFn = (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;

export interface HandleMutationErrorOptions {
  // Called when the error is an activity-guard error (active consultations or
  // requests). Most callers want this to surface the ActivityGuardModal.
  onGuard?: (guard: ActivityGuardError) => void;
  // Mapping from API error-code strings to side-effect callbacks. The matching
  // entry is invoked instead of the fallback notify; callbacks may be async.
  codeMap?: Record<string, () => void | Promise<void>>;
  // Fallback message used when the error has no matching code in codeMap.
  fallback: string;
}

// Centralizes the activity-guard / code-mapping / notify / Sentry pattern that
// otherwise duplicates across every mutation handler. Unknown error codes are
// reported to Sentry with the code as a tag so unexpected server failures
// surface in production monitoring (instead of being silently shown as a raw
// server string).
export async function handleMutationError(
  err: unknown,
  options: HandleMutationErrorOptions,
  notify: NotifyFn
): Promise<void> {
  const guard = extractActivityGuardError(err);
  if (guard) {
    options.onGuard?.(guard);
    return;
  }
  const gqlError = asGqlError(err)?.graphQLErrors?.[0];
  const code = gqlError?.extensions?.code;
  if (code && options.codeMap?.[code]) {
    await options.codeMap[code]();
    return;
  }
  Sentry.captureException(err, {
    level: 'warning',
    tags: code ? { graphql_error_code: code } : undefined,
  });
  notify(gqlError?.message || options.fallback, 'error');
}
